import { Check, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useCallingEnabled } from '@/hooks/useCalling'
import type { SubscriptionPlan } from '@/types/api/subscription'
import { cn } from '@/utils/cn'
import { formatLimitValue, isBooleanLimit } from '@/utils/quota'

interface PlanCardProps {
  plan: SubscriptionPlan
  isCurrentPlan: boolean
  /** Omit when the user may not subscribe or change plans. */
  onSubscribe?: (planCode: string) => void
  isLoading?: boolean
}

export function PlanCard({ plan, isCurrentPlan, onSubscribe, isLoading }: PlanCardProps) {
  const { enabled: callingEnabled } = useCallingEnabled()
  // Calling is hidden while it is switched off for the deployment, plan entitlement included.
  const entitlements = callingEnabled
    ? plan.entitlements
    : plan.entitlements.filter((e) => e.featureKey !== 'CALLING')

  return (
    <Card
      className={cn(
        'relative flex flex-col rounded-xl border bg-card text-card-foreground transition-all duration-200',
        isCurrentPlan
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-border hover:border-primary/40 hover:shadow-md'
      )}
    >
      {isCurrentPlan && (
        <Badge variant="default" className="absolute -top-2.5 right-4 shadow-xs">
          Current Plan
        </Badge>
      )}

      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-bold tracking-tight text-foreground">
          {plan.name}
        </CardTitle>
        {plan.description && (
          <CardDescription className="text-xs text-muted-foreground">
            {plan.description}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-extrabold tracking-tight tabular-nums text-foreground">
            ₹{plan.price}
          </span>
          <span className="text-xs text-muted-foreground">
            /{plan.durationMonths}mo
          </span>
        </div>

        <div className="border-t border-border pt-3">
          <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Included Entitlements
          </p>
          <ul className="space-y-2.5 text-xs">
            {entitlements.map((entitlement) => {
              const included = String(entitlement.limitValue).toLowerCase() !== 'false'
              return (
                <li key={entitlement.featureKey} className="flex items-start gap-2">
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                      included
                        ? 'bg-success-50 text-success-600 dark:bg-success-950/40'
                        : 'bg-muted text-muted-foreground/40'
                    )}
                  >
                    {included ? <Check className="h-3 w-3 stroke-[2.5]" /> : <X className="h-2.5 w-2.5" />}
                  </span>
                  <span
                    className={cn(
                      'leading-tight',
                      included ? 'text-foreground' : 'text-muted-foreground/60 line-through'
                    )}
                  >
                    {isBooleanLimit(entitlement.limitType)
                      ? `${entitlement.name}${included ? '' : ' — not included'}`
                      : `${formatLimitValue(entitlement.featureKey, entitlement.limitValue)} ${entitlement.name}`}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      </CardContent>

      <CardFooter className="pt-2">
        <Button
          className="w-full"
          variant={isCurrentPlan ? 'secondary' : 'default'}
          disabled={isCurrentPlan || isLoading || !onSubscribe}
          onClick={() => onSubscribe?.(plan.code)}
        >
          {isCurrentPlan ? 'Current Plan' : plan.price === 0 ? 'Start Free' : 'Select Plan'}
        </Button>
      </CardFooter>
    </Card>
  )
}